"use client";

import { useEffect, useState, useRef, ReactNode, forwardRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { transition, transitionDefaults } from "@/styles/utils";
import { FocusScope } from "@radix-ui/react-focus-scope";
import { X } from "lucide-react";

const ANIMATION_DURATION = transitionDefaults.duration;

export interface PortalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /**
   * Custom className for the close button
   */
  closeButtonClassName?: string;
  /**
   * Custom modal content className
   */
  className?: string;
  /**
   * Custom inline styles for the modal content
   */
  style?: React.CSSProperties;
  /**
   * Whether to show the close button in the top-right corner
   */
  showCloseButton?: boolean;
  /**
   * Disable backdrop click to close
   */
  disableBackdropClick?: boolean;
  /**
   * Disable ESC key to close
   */
  disableEscapeKey?: boolean;
  /**
   * Callback when close is attempted but prevented (when disableBackdropClick or disableEscapeKey is true)
   */
  onCloseAttempt?: () => void;
  /**
   * Custom z-index for the modal.
   * Accepts number (legacy) or CSS variable string.
   * @default 'var(--z-index-modal)' (1200)
   *
   * Prefer using className with utility classes instead:
   * - className="z-drawer" (1000)
   * - className="z-modal" (1200) - default
   * - className="z-modal-high" (1300)
   *
   * @deprecated Use className with z-* utility classes instead
   */
  zIndex?: number | string;
  /**
   * When true, the modal stays in the DOM after closing (hidden via CSS).
   * Useful for preserving child component state across open/close cycles.
   */
  keepMounted?: boolean;
  /**
   * Accessible label for the dialog element.
   * @default "Dialog"
   */
  ariaLabel?: string;
}

/**
 * PortalModal - A modal component that uses createPortal for rendering
 *
 * This component is designed to avoid conflicts with other modals by using
 * React's createPortal directly. It handles animations, body scroll locking,
 * and ESC key handling.
 *
 * @example
 * ```tsx
 * <PortalModal open={isOpen} onOpenChange={setIsOpen}>
 *   <PortalModal.Header>
 *     <PortalModal.Title>Select Token</PortalModal.Title>
 *     <PortalModal.Description>Select the token you want to trade</PortalModal.Description>
 *   </PortalModal.Header>
 *   <PortalModal.Content>
 *     Your content here
 *   </PortalModal.Content>
 *   <PortalModal.Footer>
 *     <Button variant="primary" onClick={() => setIsOpen(false)}>Close</Button>
 *   </PortalModal.Footer>
 * </PortalModal>
 * ```
 */
export function PortalModal({
  open,
  onOpenChange,
  children,
  closeButtonClassName,
  className,
  style,
  showCloseButton = true,
  disableBackdropClick = false,
  disableEscapeKey = false,
  onCloseAttempt,
  zIndex,
  keepMounted = false,
  ariaLabel = "Dialog",
}: PortalModalProps) {
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Compute z-index - prefer CSS variable if no custom value provided
  const computedZIndex = zIndex ?? "var(--z-index-modal)";

  // Track if modal has ever been opened (for keepMounted)
  useEffect(() => {
    if (open && !hasBeenOpened) setHasBeenOpened(true);
  }, [open, hasBeenOpened]);

  // Handle rendering lifecycle and animation timing
  useEffect(() => {
    if (open) {
      setShouldRender(true);
      const animTimer = window.setTimeout(() => setIsAnimating(true), 10);
      return () => window.clearTimeout(animTimer);
    }

    setIsAnimating(false);
    if (!keepMounted) {
      const renderTimer = window.setTimeout(() => setShouldRender(false), ANIMATION_DURATION);
      return () => window.clearTimeout(renderTimer);
    }
    return undefined;
  }, [open, keepMounted]);

  // Lock body scroll while the modal is open
  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  // Handle ESC key
  useEffect(() => {
    if (!open) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (disableEscapeKey) {
          onCloseAttempt?.();
        } else {
          onOpenChange(false);
        }
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open, onOpenChange, disableEscapeKey, onCloseAttempt]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mdBreakpoint = getComputedStyle(document.documentElement).getPropertyValue("--breakpoint-md").trim();
    const parsedMd = parseInt(mdBreakpoint.replace("px", ""), 10);
    const effectiveMobileBreakpoint = Number.isNaN(parsedMd) ? 768 : parsedMd;

    const mediaQuery = window.matchMedia(`(max-width: ${effectiveMobileBreakpoint - 1}px)`);
    const updateViewportFlag = (event?: MediaQueryListEvent) => {
      setIsMobileViewport(event ? event.matches : mediaQuery.matches);
    };

    updateViewportFlag();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateViewportFlag);
      return () => mediaQuery.removeEventListener("change", updateViewportFlag);
    }

    mediaQuery.addListener(updateViewportFlag);
    return () => mediaQuery.removeListener(updateViewportFlag);
  }, []);

  const handleBackdropClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (disableBackdropClick) {
      onCloseAttempt?.();
    } else {
      onOpenChange(false);
    }
  };

  // Respect consumer-provided sizing utilities so sheet defaults don't override them.
  const classTokens = typeof className === "string" ? className.split(/\s+/).filter(Boolean) : [];
  const hasBaseClass = (token: string) => !token.includes(":");
  const hasCustomWidth = classTokens.some((token) => {
    if (!hasBaseClass(token)) return false;
    return token.startsWith("w-") || token.startsWith("w[") || token.startsWith("max-w-") || token.startsWith("max-w[");
  });
  const hasCustomHeight = classTokens.some((token) => {
    if (!hasBaseClass(token)) return false;
    return token.startsWith("h-") || token.startsWith("h[");
  });
  const hasCustomMaxHeight = classTokens.some((token) => {
    if (!hasBaseClass(token)) return false;
    return token.startsWith("max-h-") || token.startsWith("max-h[");
  });

  const modalStyles: React.CSSProperties = {
    backgroundColor: "var(--color-cede-bg-modal)",
  };

  if (isMobileViewport && !hasCustomWidth) {
    modalStyles.width = "100vw";
    modalStyles.maxWidth = "100vw";
  }

  if (isMobileViewport && !hasCustomHeight) {
    modalStyles.height = "80vh";
  }

  if (isMobileViewport && !hasCustomMaxHeight) {
    modalStyles.maxHeight = "80vh";
  }

  const effectiveShouldRender = keepMounted ? hasBeenOpened : shouldRender;
  if (!effectiveShouldRender) return null;

  const isHidden = keepMounted && !open && !isAnimating;

  return (
    <>
      {typeof document !== "undefined" &&
        createPortal(
          <div
            className={cn(
              "fixed inset-0 flex justify-center",
              isMobileViewport ? "items-end" : "items-center",
              transition(),
              isAnimating ? "opacity-100" : "opacity-0",
            )}
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(8px)",
              zIndex: computedZIndex,
              ...(isHidden && { visibility: "hidden", pointerEvents: "none" }),
            }}
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            {/* Backdrop */}
            <div className="absolute inset-0" onClick={handleBackdropClick} aria-hidden="true" />

            {/* Modal Content */}
            <div
              ref={modalRef}
              className={cn(
                "relative flex flex-col overflow-hidden z-10 rounded-t-3xl rounded-b-none sm:rounded-2xl transform py-6 gap-4 backdrop-blur-xl",
                !hasCustomWidth && "w-full sm:w-[500px] sm:max-w-[90vw]",
                !isMobileViewport && !hasCustomMaxHeight && "max-h-[90vh]",
                transition(),
                isAnimating
                  ? "opacity-100 translate-y-0 sm:scale-100"
                  : "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95",
                className,
              )}
              style={{ ...modalStyles, ...style }}
              role="dialog"
              aria-modal="true"
              aria-label={ariaLabel}
              onClick={(e) => e.stopPropagation()}
            >
              <FocusScope loop trapped={isAnimating} className="contents">
                {/* Close Button */}
                {showCloseButton && (
                  <button
                    onClick={() => onOpenChange(false)}
                    className={cn(
                      "absolute top-5 right-5 sm:top-6 sm:right-6 z-10 p-2 rounded-lg bg-transparent hover:bg-cede-primary/10 transition-colors text-cede-text-secondary hover:text-cede-primary cursor-pointer focus-visible:ring-2 focus-visible:ring-cede-primary",
                      closeButtonClassName,
                    )}
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}

                {children}
              </FocusScope>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * PortalModal.Header - Header section of the modal
 */
function PortalModalHeader({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("flex flex-col gap-2", className)} {...props}>
      {children}
    </div>
  );
}

/**
 * PortalModal.Title - Title component for the modal header
 */
function PortalModalTitle({ className, children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={cn("text-xl font-semibold text-cede-text-primary px-6", className)} {...props}>
      {children}
    </h3>
  );
}

/**
 * PortalModal.Description - Description component for the modal header
 */
function PortalModalDescription({ className, children, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={cn("text-sm text-cede-text-secondary px-6", className)} {...props}>
      {children}
    </p>
  );
}

/**
 * PortalModal.Content - Content section of the modal
 * [scrollbar-gutter:stable] keeps a stable width for the scrollbar that's why we have to use pl-6 pr-[18px]
 */
type PortalModalContentProps = React.HTMLAttributes<HTMLDivElement> & { scrollable?: boolean };

const PortalModalContent = forwardRef<HTMLDivElement, PortalModalContentProps>(
  ({ className, children, scrollable = false, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col flex-1 overflow-hidden pl-6 pr-6",
          scrollable && "overflow-y-auto scrollbar-visible [scrollbar-gutter:stable] pr-[18px]",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);
PortalModalContent.displayName = "PortalModalContent";

/**
 * PortalModal.Footer - Footer section of the modal
 */
function PortalModalFooter({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("flex justify-end gap-2 px-6", className)} {...props}>
      {children}
    </div>
  );
}

// Attach subcomponents
PortalModal.Header = PortalModalHeader;
PortalModal.Title = PortalModalTitle;
PortalModal.Description = PortalModalDescription;
PortalModal.Content = PortalModalContent;
PortalModal.Footer = PortalModalFooter;
